// src/components/operationsAssociate/IssueItemModal.jsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";

const API_URL = "https://ims.holdthechild.net";

export default function IssueItemModal({ closeModal, onIssue, item }) {
    const [quantity, setQuantity] = useState(1);
    const [recipientType, setRecipientType] = useState("project"); // Default to project for streamlined workflow
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [loading, setLoading] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [projectList, setProjectList] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem("token");
            if (!token) return toast.error("Authentication failed.");

            try {
                const [staffRes, projectsRes] = await Promise.all([
                    axios.get(`${API_URL}/api/staff`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                setStaffList(staffRes.data.data || []);
                setProjectList(projectsRes.data.data || []);
            } catch (err) {
                toast.error("Failed to load recipient lists.");
                console.error(err);
            }
        };
        fetchData();
    }, []);

    const staffOptions = staffList.map(s => ({
        value: s._id,
        label: `${s.firstName} ${s.lastName}`,
    }));

    const projectOptions = projectList.map(p => ({
        value: p._id,
        label: p.projectTitle,
        programManagerId: p.assignedProgramManager,
    }));

    // Effect to automatically select the program manager when a project is chosen
    useEffect(() => {
        if (selectedProject && recipientType === 'project' && staffList.length > 0) {
            const pm = staffList.find(s => s._id === selectedProject.programManagerId);
            if (pm) {
                setSelectedStaff({
                    value: pm._id,
                    label: `${pm.firstName} ${pm.lastName}`,
                });
            } else {
                setSelectedStaff(null);
            }
        } else if (recipientType === 'staff') {
            setSelectedStaff(null); // Clear staff if switching to staff-only selection
        }
    }, [selectedProject, recipientType, staffList]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const token = localStorage.getItem("token");

        if (quantity <= 0) {
            toast.error("Quantity must be a positive number.");
            setLoading(false);
            return;
        }

        // 1. Create the data payload for the new IssuedItem backend endpoint.
        const data = {
            itemId: item._id,
            quantity: Number(quantity),
            // The recipient fields will be added below.
        };

        // 2. Determine the correct recipient key based on the selected type.
        if (recipientType === "staff") {
            if (!selectedStaff) {
                toast.error("Please select a staff member.");
                setLoading(false);
                return;
            }
            // Use the correct key for the new IssuedItem model.
            data.issuedToStaff = selectedStaff.value;
        } else {
            if (!selectedProject) {
                toast.error("Please select a project.");
                setLoading(false);
                return;
            }
            // Use the correct key for the new IssuedItem model.
            data.issuedToProject = selectedProject.value;
        }

        try {
            // 3. Send the updated data payload to the backend.
            await axios.post(`${API_URL}/api/inventory/issue`, data, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success(`Successfully issued ${quantity} ${item.name}.`);
            onIssue();
            closeModal();
        } catch (error) {
            const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
            toast.error(`Error: ${errorMessage} 😥`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
                    <h2 className="text-2xl font-bold text-orange-600">Issue {item.name}</h2>
                    <button type="button" onClick={closeModal} className="text-3xl text-gray-400 hover:text-red-500 font-light">&times;</button>
                </div>

                <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-md">
                        <p className="font-semibold">Current Stock: <span className="text-green-600">{item.inStock} {item.unitOfMeasure}</span></p>
                        <p className="font-semibold">Reserved: <span className="text-orange-600">{item.reserved} {item.unitOfMeasure}</span></p>
                    </div>

                    <div>
                        <label className="block mb-1 font-medium text-gray-700">Quantity to Issue</label>
                        <input
                            type="number"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            min="1"
                            max={item.inStock}
                            className="w-full border rounded-md px-3 py-2"
                            required
                        />
                    </div>

                    <div>
                        <label className="block mb-1 font-medium text-gray-700">Issue To:</label>
                        <div className="flex gap-4">
                            <button
                                type="button"
                                onClick={() => setRecipientType('project')}
                                className={`px-4 py-2 rounded-md ${recipientType === 'project' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                            >
                                Project
                            </button>
                            <button
                                type="button"
                                onClick={() => setRecipientType('staff')}
                                className={`px-4 py-2 rounded-md ${recipientType === 'staff' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-800'}`}
                            >
                                Staff
                            </button>
                        </div>
                    </div>

                    {recipientType === 'project' ? (
                        <>
                            <div>
                                <label className="block mb-1 font-medium text-gray-700">Select Project</label>
                                <Select
                                    options={projectOptions}
                                    onChange={setSelectedProject}
                                    value={selectedProject}
                                    placeholder="Select a project..."
                                    isClearable
                                />
                            </div>
                            {/* <div>
                                <label className="block mb-1 font-medium text-gray-700">Assigned Program Manager</label>
                                <Select
                                    options={staffOptions}
                                    value={selectedStaff}
                                    placeholder="Auto-filled..."
                                    isDisabled
                                />
                            </div> */}
                        </>
                    ) : (
                        <div>
                            <label className="block mb-1 font-medium text-gray-700">Select Staff Member</label>
                            <Select
                                options={staffOptions}
                                onChange={setSelectedStaff}
                                value={selectedStaff}
                                placeholder="Select a staff member..."
                                isClearable
                            />
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-200">
                    <button type="button" onClick={closeModal} className="px-5 py-2 rounded-md border border-gray-300 hover:bg-gray-50" disabled={loading}>Cancel</button>
                    <button type="submit" className="px-5 py-2 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50" disabled={loading || (recipientType === 'project' && !selectedProject) || (recipientType === 'staff' && !selectedStaff)}>
                        {loading ? "Issuing..." : "Issue Item"}
                    </button>
                </div>
            </form>
        </div>
    );
}